/**
 * FDS-ID API Client
 *
 * Client for the optional fds-id server that provides enhanced account management:
 * - Server-validated SIWE (Sign-In with Ethereum) with session-bound nonces
 * - Swarm backup storage
 * - ENS text record management
 *
 * IMPORTANT: This is OPTIONAL. Core functionality (keystore login, account creation,
 * file import) MUST work without fds-id server. The client gracefully falls back
 * to local operations when the server is unavailable.
 */

import type {
  FDSIdConfig,
  NonceResponse,
  SIWEVerifyRequest,
  SIWEVerifyResult,
  KeystoreValidationResult,
  ParsedENS,
  BackupUploadResult,
  HealthResponse,
  TrialStatusResponse,
  RegisterAccountRequest,
  RegisterAccountResponse,
  StampStatusResponse,
  UploadKeystoreRequest,
  UploadKeystoreResponse,
} from './types'
import { getCircuitBreaker, withRetry } from './circuit-breaker'
import { getOrCreateSessionId, storeNonceWithSession } from './session'
import { getSettings } from '../storage'

// Default configuration
const DEFAULT_CONFIG: FDSIdConfig = {
  baseUrl: import.meta.env.VITE_FDS_ID_URL || 'https://id.fairdatasociety.org',
  timeout: Number(import.meta.env.VITE_FDS_ID_TIMEOUT) || 10000,
  enabled: import.meta.env.VITE_FDS_ID_ENABLED !== 'false',
}

/**
 * Check if self-sovereign mode is enabled
 */
export function isSelfSovereignMode(): boolean {
  const settings = getSettings()
  return settings.selfSovereign === true
}

/**
 * Validate fds-id URL - HTTPS required in production
 */
function validateFdsIdUrl(url: string): void {
  // Resolve relative paths (e.g. /api/fds-id) against page origin for Vite proxy
  const parsed = new URL(url, window.location.origin)
  if (parsed.protocol !== 'https:' && !import.meta.env.DEV) {
    throw new Error('fds-id requires HTTPS in production')
  }
}

/**
 * Check if fds-id integration is enabled
 * Returns false if self-sovereign mode is active
 */
export function fdsIdEnabled(): boolean {
  // Self-sovereign mode disables fds-id convenience API
  if (isSelfSovereignMode()) {
    return false
  }
  return DEFAULT_CONFIG.enabled ?? true
}

/**
 * Get the configured fds-id URL
 */
export function getFdsIdUrl(): string {
  return DEFAULT_CONFIG.baseUrl
}

/**
 * FDS-ID API Client class
 */
class FDSIdClient {
  private readonly config: FDSIdConfig

  constructor(config: Partial<FDSIdConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config }

    // Validate URL on construction
    if (this.config.enabled) {
      validateFdsIdUrl(this.config.baseUrl)
    }
  }

  /**
   * Check if fds-id server is available
   */
  async isAvailable(): Promise<boolean> {
    if (!this.config.enabled) return false

    try {
      const response = await this.fetch('/health', { method: 'GET' })
      const data = (await response.json()) as HealthResponse
      return data.status === 'ok'
    } catch {
      return false
    }
  }

  /**
   * Get a server-validated nonce for SIWE
   */
  async getNonce(): Promise<NonceResponse> {
    const sessionId = getOrCreateSessionId()

    const response = await this.fetch('/api/auth/nonce', {
      method: 'GET',
    })

    if (!response.ok) {
      throw new Error(`Failed to get nonce: ${response.status}`)
    }

    const raw = await response.json() as { nonce: string; expiresIn?: number; sessionId?: string; expiresAt?: number }

    // Map server response to NonceResponse
    // Production server returns {nonce, expiresIn} (seconds), map to {nonce, sessionId, expiresAt}
    const data: NonceResponse = {
      nonce: raw.nonce,
      // Always use local session ID for local nonce storage.
      // The server has its own nonce tracking; verifyAndConsumeNonce()
      // checks against getSessionId() which returns the local ID.
      sessionId: sessionId,
      expiresAt: raw.expiresAt || (raw.expiresIn ? Date.now() + raw.expiresIn * 1000 : Date.now() + 300_000),
    }

    // Store nonce with session binding
    storeNonceWithSession(data.nonce, data.sessionId, data.expiresAt)

    return data
  }

  /**
   * Verify SIWE signature on server
   */
  async verifySIWE(message: string, signature: string): Promise<SIWEVerifyResult> {
    const sessionId = getOrCreateSessionId()

    const request: SIWEVerifyRequest = { message, signature, sessionId }

    const response = await this.fetch('/api/auth/verify', {
      method: 'POST',
      body: JSON.stringify(request),
    })

    if (!response.ok) {
      const error = await response.text()
      return { success: false, error }
    }

    return (await response.json()) as SIWEVerifyResult
  }

  /**
   * Validate a keystore format
   */
  async validateKeystore(keystore: object): Promise<KeystoreValidationResult> {
    const response = await this.fetch('/api/keystore/validate', {
      method: 'POST',
      body: JSON.stringify({ keystore }),
    })

    if (!response.ok) {
      return { valid: false, error: `Validation failed: ${response.status}` }
    }

    return (await response.json()) as KeystoreValidationResult
  }

  /**
   * Parse an ENS name
   */
  async parseENSName(name: string): Promise<ParsedENS> {
    const response = await this.fetch(`/api/ens/parse?name=${encodeURIComponent(name)}`, {
      method: 'GET',
    })

    if (!response.ok) {
      throw new Error(`ENS parse failed: ${response.status}`)
    }

    return (await response.json()) as ParsedENS
  }

  /**
   * Upload keystore backup to Swarm
   */
  async uploadBackup(keystoreJson: string, subdomain: string): Promise<BackupUploadResult> {
    const sessionId = getOrCreateSessionId()

    const response = await this.fetch('/api/backup/upload', {
      method: 'POST',
      body: JSON.stringify({ keystore: keystoreJson, subdomain, sessionId }),
    })

    if (!response.ok) {
      const error = await response.text()
      return { success: false, error }
    }

    return (await response.json()) as BackupUploadResult
  }

  /**
   * Download keystore backup from Swarm
   */
  async downloadBackup(reference: string): Promise<string> {
    const response = await this.fetch(`/api/backup/download?reference=${encodeURIComponent(reference)}`, {
      method: 'GET',
    })

    if (!response.ok) {
      throw new Error(`Backup download failed: ${response.status}`)
    }

    const data = (await response.json()) as { keystore: string }
    return data.keystore
  }

  /**
   * Request a trial stamp (requires SIWE authentication)
   */
  async requestTrialStamp(message: string, signature: string): Promise<TrialStatusResponse> {
    const sessionId = getOrCreateSessionId()

    const response = await this.fetch('/api/stamp/trial', {
      method: 'POST',
      body: JSON.stringify({ message, signature, sessionId }),
    })

    const data = await response.json() as TrialStatusResponse

    if (!response.ok) {
      return { hasTrialed: true, error: data.error || `Request failed: ${response.status}` }
    }

    return data
  }

  /**
   * Get trial stamp status (requires SIWE authentication)
   */
  async getTrialStatus(message: string, signature: string): Promise<TrialStatusResponse> {
    const sessionId = getOrCreateSessionId()

    const response = await this.fetch('/api/stamp/trial/status', {
      method: 'POST',
      body: JSON.stringify({ message, signature, sessionId }),
    })

    const data = await response.json() as TrialStatusResponse

    if (!response.ok) {
      return { hasTrialed: false, error: data.error || `Request failed: ${response.status}` }
    }

    return data
  }

  /**
   * Get the dedicated backup stamp ID from the server
   * Returns null if no backup stamp is configured
   */
  async getBackupStamp(): Promise<string | null> {
    try {
      const response = await this.fetch('/api/backup/stamp', { method: 'GET' })
      if (!response.ok) return null
      const data = (await response.json()) as { stampId: string }
      return data.stampId || null
    } catch {
      return null
    }
  }

  /**
   * Register account via fds-id (ENS + stamp + GSOC in one call)
   * Uses extended timeout since server does on-chain ENS + GSOC mining
   */
  async register(req: RegisterAccountRequest): Promise<RegisterAccountResponse> {
    const response = await this.fetch('/api/account/register', {
      method: 'POST',
      body: JSON.stringify(req),
    }, 300_000) // Extended timeout: ENS (3 txs ~60s) + stamp (~10s) + GSOC mining (~150s)

    if (!response.ok) {
      const data = await response.json().catch(() => ({ error: `HTTP ${response.status}` })) as { error?: string }
      throw new Error(data.error || `Registration failed: ${response.status}`)
    }

    return (await response.json()) as RegisterAccountResponse
  }

  /**
   * Repair missing GSOC inbox ENS records.
   * Calls server to mine GSOC key and set ENS text records.
   */
  async repairInbox(subdomain: string, publicKey: string): Promise<{ repaired: boolean; inbox?: { targetOverlay: string; baseIdentifier: string; proximity: number } }> {
    const response = await this.fetch('/api/account/repair-inbox', {
      method: 'POST',
      body: JSON.stringify({ subdomain, publicKey }),
    }, 300_000) // Extended timeout: GSOC mining + ENS txs

    if (!response.ok) {
      const data = await response.json().catch(() => ({ error: `HTTP ${response.status}` })) as { error?: string }
      throw new Error(data.error || `Inbox repair failed: ${response.status}`)
    }

    return (await response.json()) as { repaired: boolean; inbox?: { targetOverlay: string; baseIdentifier: string; proximity: number } }
  }

  /**
   * Poll stamp status (exponential backoff done by caller)
   */
  async pollStampStatus(batchId: string): Promise<StampStatusResponse> {
    const response = await this.fetch(`/api/account/stamp/${batchId}/status`, {
      method: 'GET',
    })

    if (!response.ok) {
      throw new Error(`Stamp status check failed: ${response.status}`)
    }

    return (await response.json()) as StampStatusResponse
  }

  /**
   * Upload keystore to Swarm via fds-id (server handles upload + ENS backup record)
   */
  async uploadKeystore(req: UploadKeystoreRequest): Promise<UploadKeystoreResponse> {
    const response = await this.fetch('/api/account/upload-keystore', {
      method: 'POST',
      body: JSON.stringify(req),
    }, 60_000) // Extended timeout: Swarm upload + ENS record update

    if (!response.ok) {
      const data = await response.json().catch(() => ({ error: `HTTP ${response.status}` })) as { error?: string }
      throw new Error(data.error || `Keystore upload failed: ${response.status}`)
    }

    return (await response.json()) as UploadKeystoreResponse
  }

  /**
   * Internal fetch with timeout and retry
   */
  private async fetch(path: string, options: RequestInit, timeoutMs?: number): Promise<Response> {
    const url = `${this.config.baseUrl}${path}`
    const timeout = timeoutMs || this.config.timeout || 10000

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      return await withRetry(async () => {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            ...options.headers,
          },
        })
        return response
      })
    } finally {
      clearTimeout(timeoutId)
    }
  }
}

// Singleton client instance
let clientInstance: FDSIdClient | null = null

/**
 * Get the FDS-ID client instance
 */
export function getFdsIdClient(): FDSIdClient {
  if (!clientInstance) {
    clientInstance = new FDSIdClient()
  }
  return clientInstance
}

/**
 * Reset the client instance (for testing)
 */
export function resetFdsIdClient(): void {
  clientInstance = null
}

/**
 * Helper to make fds-id calls with circuit breaker fallback
 */
export async function withFdsIdFallback<T>(
  serverCall: () => Promise<T>,
  localFallback: () => T | Promise<T>
): Promise<T> {
  if (!fdsIdEnabled()) {
    return localFallback()
  }

  const circuitBreaker = getCircuitBreaker()
  return circuitBreaker.call(serverCall, localFallback)
}
